import { useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { diaCurto, reais } from '../../premiacao';
import type { ChequeDaPessoa, Programa } from '../../premiacao';
import { cancelarVenda, corrigirVenda } from '../../vendas';
import { NOME_DO_PROGRAMA, PROGRAMAS } from './conta-da-semana';
import './conferencia.css';

/**
 * TUDO O QUE UM VENDEDOR FEZ NA SEMANA — a mesma gaveta na Conferência e na Premiação (dono,
 * 24/10/2026): por programa (vitamina, pré-vencido, paradão), quanto vendeu no HOS × quanto lançou,
 * o que bateu e o que não bateu, canceladas, devoluções e as FOTOS dos pré-vencidos. Quem confere
 * pode EDITAR (quantidade, dia, preço — a venda original fica guardada e nasce uma nova ligada a
 * ela) ou CANCELAR, sempre com motivo. Semana já paga não aceita: o servidor recusa.
 */

type Aba = 'conferidos' | 'nao_bateu' | 'canceladas' | 'devolucoes' | 'fotos';

type ItemQueNaoBateu = {
  chave: string;
  programa: Programa;
  produto: { nome: string; codigo: string | null };
  dia: string;
  /** O dia gravado no lançamento (no "dia errado" é diferente do dia da venda no HOS). */
  diaDoLancamento: string;
  qtd: number;
  foto: string | null;
  lote: string | null;
  regra: string;
  texto: string;
  valor: string;
  vendaIds: readonly string[];
  jaPaga: boolean;
};

/** Cupom vindo da cópia do 1.0 chega como "migracao_1_0|dia|número": mostra só o número. */
const cupom = (c: string) => c.split('|').pop() ?? c;

export function VendasDaPessoa({
  c,
  semRelatorio,
  sessao,
  podeCancelar,
  podeEditar,
  aoMudar,
}: {
  c: ChequeDaPessoa;
  semRelatorio: ReadonlySet<string>;
  sessao: Sessao;
  podeCancelar: boolean;
  podeEditar: boolean;
  aoMudar: () => void;
}) {
  const [aba, setAba] = useState<Aba>('conferidos');
  const [programa, setPrograma] = useState<Programa | 'todos'>('todos');

  const doPrograma = <T extends { programa: Programa }>(xs: readonly T[]) =>
    programa === 'todos' ? xs : xs.filter((x) => x.programa === programa);
  const conferidos = [
    ...doPrograma([
      ...c.linhas.bate.map((l) => ({ ...l, tipo: 'bate' as const })),
      ...c.linhas.abaixoDoCard.map((l) => ({ ...l, tipo: 'abaixo' as const })),
    ]),
  ].sort((a, b) => a.dia.localeCompare(b.dia));
  const naoBateu: ItemQueNaoBateu[] = [
    ...doPrograma(c.linhas.naoVendeu).map((l) => ({
      chave: `v${l.dia}${l.produto.nome}`,
      programa: l.programa,
      produto: l.produto,
      dia: l.dia,
      diaDoLancamento: l.dia,
      qtd: l.qtd,
      foto: l.foto,
      lote: l.lote,
      regra: semRelatorio.has(l.dia) ? 'Aguardando o HOS' : 'Lançou e não vendeu',
      texto: semRelatorio.has(l.dia)
        ? 'a venda do HOS deste dia ainda não chegou'
        : 'lançado no InovaPay, mas não há venda desse produto para esta pessoa no HOS',
      valor: `app: ${reais(l.pago)}`,
      vendaIds: l.vendaIds,
      jaPaga: l.jaPaga,
    })),
    ...doPrograma(c.linhas.diaErrado).map((l) => ({
      chave: `d${l.dia}${l.produto.nome}`,
      programa: l.programa,
      produto: l.produto,
      dia: l.dia,
      diaDoLancamento: l.lancouEm,
      qtd: l.qtd,
      foto: l.foto,
      lote: l.lote,
      regra: 'Lançou em dia diferente',
      texto: `lançou em ${diaCurto(l.lancouEm)}, vendeu no HOS em ${diaCurto(l.dia)}`,
      valor: `perdeu ${reais(l.perdeu)}`,
      vendaIds: l.vendaIds,
      jaPaga: l.jaPaga,
    })),
    ...doPrograma(c.linhas.loteDoMesmoDia ?? []).map((l) => ({
      chave: `m${l.dia}${l.produto.nome}`,
      programa: l.programa,
      produto: l.produto,
      dia: l.dia,
      diaDoLancamento: l.dia,
      qtd: l.qtd,
      foto: l.foto,
      lote: l.lote,
      regra: 'Lote vendido no dia em que entrou',
      texto: `o lote entrou no sistema em ${diaCurto(l.loteCriadoEm)} e foi vendido no mesmo dia`,
      valor: `perdeu ${reais(l.perdeu)}`,
      vendaIds: l.vendaIds,
      jaPaga: l.jaPaga,
    })),
    ...doPrograma(c.linhas.naoLancou).map((l) => ({
      chave: `n${l.dia}${l.produto.nome}`,
      programa: l.programa,
      produto: l.produto,
      dia: l.dia,
      diaDoLancamento: l.dia,
      qtd: l.qtd,
      foto: null,
      lote: null,
      regra: 'Vendeu e não lançou',
      texto: `vendeu no HOS${l.cupons.length ? ` (cupom ${l.cupons.map(cupom).join(', ')})` : ''} e não lançou no InovaPay`,
      valor: `perdeu ${reais(l.perdeu)}`,
      vendaIds: [],
      jaPaga: false,
    })),
  ].sort((a, b) => a.dia.localeCompare(b.dia));
  const canceladas = doPrograma(c.linhas.canceladas);
  const devolvidos = doPrograma(c.linhas.devolvidos);
  const fotos = [
    ...conferidos.map((l) => ({ ...l, ok: true })),
    ...naoBateu.map((l) => ({ ...l, ok: false })),
  ]
    .filter((f) => f.foto)
    .map((f) => ({
      foto: f.foto,
      produto: f.produto.nome,
      dia: f.dia,
      lote: f.lote,
      ok: f.ok,
    }));
  const pvSemFoto = [...conferidos, ...naoBateu].filter(
    (l) => l.programa === 'pre_vencido' && !l.foto && l.vendaIds.length > 0,
  ).length;

  const abas: readonly [Aba, string, number][] = [
    ['conferidos', 'Conferidos', conferidos.length],
    ['nao_bateu', 'Não bateu', naoBateu.length],
    ['canceladas', 'Canceladas', canceladas.length],
    ['devolucoes', 'Devoluções', devolvidos.length],
    ['fotos', 'Fotos', fotos.length],
  ];
  const porPrograma = PROGRAMAS.map((x) => {
    const de = <T extends { programa: Programa; qtd: number }>(xs: readonly T[]) =>
      xs.filter((l) => l.programa === x.id).reduce((s, l) => s + l.qtd, 0);
    const naoLancou = de(c.linhas.naoLancou);
    const lancouEVendeu =
      de(c.linhas.bate) +
      de(c.linhas.abaixoDoCard) +
      de(c.linhas.diaErrado) +
      de(c.linhas.loteDoMesmoDia ?? []);
    return {
      id: x.id,
      nome: x.nome,
      vendeu: lancouEVendeu + naoLancou,
      lancou: lancouEVendeu + de(c.linhas.naoVendeu),
      premio: c.tipos[x.id].devido,
    };
  });
  const acoes = { pode: podeCancelar, podeEditar, sessao, aoMudar };

  return (
    <>
      <table data-sem-ordenar className="vp-resumo">
        <thead>
          <tr>
            <th className="vp-esq">PROGRAMA</th>
            <th>VENDEU NO HOS</th>
            <th>LANÇOU</th>
            <th>PRÊMIO</th>
          </tr>
        </thead>
        <tbody>
          {porPrograma.map((p) => (
            <tr
              key={p.id}
              className={programa === p.id ? 'vp-ativo' : ''}
              onClick={() => setPrograma(programa === p.id ? 'todos' : p.id)}
            >
              <td className="vp-esq">
                <span className={`cf-tag cf-tag-${p.id}`}>{p.nome}</span>
              </td>
              <td>{p.vendeu} un</td>
              <td className={p.lancou !== p.vendeu ? 'vp-difere' : ''}>{p.lancou} un</td>
              <td>
                <strong>{reais(p.premio)}</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cf-abas" role="tablist">
        {abas.map(([id, rotulo, n]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={aba === id}
            className={aba === id ? 'cf-aba-ativa' : ''}
            onClick={() => setAba(id)}
          >
            {rotulo} <span>{n}</span>
          </button>
        ))}
      </div>
      <div className="cf-filtros cf-filtros-gaveta">
        {(['todos', 'vitamina', 'pre_vencido', 'paradao'] as const).map((x) => (
          <button
            key={x}
            type="button"
            className={`cf-chip${programa === x ? ' cf-chip-ativo' : ''}`}
            aria-pressed={programa === x}
            onClick={() => setPrograma(x)}
          >
            {x === 'todos' ? 'Todos' : NOME_DO_PROGRAMA[x]}
          </button>
        ))}
      </div>

      {aba === 'conferidos' && (
        <ul className="cf-itens">
          {conferidos.length === 0 && <li className="pr-nada">Nada conferido neste filtro.</li>}
          {conferidos.map((l, i) => (
            <li key={`${l.dia}${l.produto.nome}${String(i)}`}>
              <div className="cf-item-topo">
                <span className={`cf-tag cf-tag-${l.programa}`}>
                  {NOME_DO_PROGRAMA[l.programa]}
                </span>
                <strong>{l.produto.nome}</strong>
                <span className="cf-item-valor">
                  {l.tipo === 'bate' ? reais(l.devido) : `app: ${reais(l.pago)}`}
                </span>
              </div>
              <div className="cf-item-linha">
                {diaCurto(l.dia)} · {l.qtd} un · {reais(l.precoUnit)} cada
                {l.produto.codigo ? ` · cód ${l.produto.codigo}` : ''}
                {l.lote ? ` · lote ${l.lote}` : ''}
              </div>
              {(l.vendas?.length ?? 0) > 0 && (
                <div className="cf-item-hos">
                  No HOS:{' '}
                  {(l.vendas ?? [])
                    .map(
                      (v) =>
                        `cupom ${cupom(v.cupom)}${v.hora ? ` às ${v.hora.slice(0, 5)}` : ''} (${String(v.qtd)} × ${reais(v.precoUnit)})`,
                    )
                    .join(' · ')}
                </div>
              )}
              {l.tipo === 'abaixo' && (
                <div className="cf-item-alerta">
                  {l.motivo ?? 'vendido abaixo do card: não premia'}
                </div>
              )}
              <Foto id={l.foto} programa={l.programa} />
              <Acoes
                {...acoes}
                ids={l.vendaIds}
                jaPaga={l.jaPaga}
                venda={{ qtd: l.qtd, dia: l.dia, preco: l.precoUnit, programa: l.programa }}
              />
            </li>
          ))}
        </ul>
      )}

      {aba === 'nao_bateu' && (
        <ul className="cf-itens">
          {naoBateu.length === 0 && <li className="pr-nada">Tudo bateu neste filtro.</li>}
          {naoBateu.map((l) => (
            <li key={l.chave} className="cf-item-ruim">
              <div className="cf-item-topo">
                <span className={`cf-tag cf-tag-${l.programa}`}>
                  {NOME_DO_PROGRAMA[l.programa]}
                </span>
                <strong>{l.produto.nome}</strong>
                <span className="cf-item-valor">{l.valor}</span>
              </div>
              <div className="cf-item-linha">
                {diaCurto(l.dia)} · {l.qtd} un{l.lote ? ` · lote ${l.lote}` : ''}
              </div>
              <div className="cf-item-alerta">
                <strong>{l.regra}:</strong> {l.texto}
              </div>
              <Foto id={l.foto} programa={l.vendaIds.length ? l.programa : 'vitamina'} />
              <Acoes
                {...acoes}
                ids={l.vendaIds}
                jaPaga={l.jaPaga}
                venda={{
                  qtd: l.qtd,
                  dia: l.diaDoLancamento,
                  preco: null,
                  programa: l.programa,
                  diaNoHos: l.diaDoLancamento !== l.dia ? l.dia : undefined,
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {aba === 'canceladas' && (
        <ul className="cf-itens">
          {canceladas.length === 0 && <li className="pr-nada">Nenhuma cancelada neste filtro.</li>}
          {canceladas.map((l, i) => (
            <li key={`${l.dia}${l.produto.nome}${String(i)}`} className="cf-item-ruim">
              <div className="cf-item-topo">
                <span className={`cf-tag cf-tag-${l.programa}`}>
                  {NOME_DO_PROGRAMA[l.programa]}
                </span>
                <strong>{l.produto.nome}</strong>
                <span className="cf-item-valor pr-vermelho">
                  {l.desconto ? reais(-l.desconto) : 'sem desconto'}
                </span>
              </div>
              <div className="cf-item-linha">
                {diaCurto(l.dia)} · {l.qtd} un · o app tinha gravado {reais(l.pagoOrig)}
              </div>
              <div className="cf-item-hos">
                {l.semPremio
                  ? 'não havia venda no HOS: sai sem desconto'
                  : 'havia venda no HOS: o prêmio dela vira desconto'}
                {l.motivo ? ` · motivo: ${l.motivo}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      {aba === 'devolucoes' && (
        <ul className="cf-itens">
          {devolvidos.length === 0 && <li className="pr-nada">Nenhuma devolução neste filtro.</li>}
          {devolvidos.map((d, i) => (
            <li key={`${d.dia}${d.cupom}${String(i)}`} className="cf-item-ruim">
              <div className="cf-item-topo">
                <span className={`cf-tag cf-tag-${d.programa}`}>
                  {NOME_DO_PROGRAMA[d.programa]}
                </span>
                <strong>{d.produto.nome}</strong>
                <span className="cf-item-valor pr-vermelho">{reais(-d.desconto)}</span>
              </div>
              <div className="cf-item-linha">
                devolvido em {diaCurto(d.dia)} · {d.qtd} un · {reais(d.valor)} · venda do cupom{' '}
                {cupom(d.cupom)}
              </div>
              <div className="cf-item-hos">
                {d.deSemanaPaga
                  ? 'Ajuste: a semana dessa devolução já estava paga sem ela — desconta neste fechamento.'
                  : 'Tira a comissão daquela venda, não o preço.'}
              </div>
            </li>
          ))}
        </ul>
      )}

      {aba === 'fotos' && (
        <>
          {fotos.length === 0 ? (
            <p className="pr-nada">Nenhuma foto neste filtro.</p>
          ) : (
            <div className="cf-fotos">
              {fotos.map((f, i) => (
                <a
                  key={`${f.foto ?? ''}${String(i)}`}
                  href={`/api/evidencias/${f.foto ?? ''}`}
                  target="_blank"
                  rel="noreferrer"
                  className={f.ok ? '' : 'cf-foto-ruim'}
                >
                  <img
                    src={`/api/evidencias/${f.foto ?? ''}`}
                    alt={`Foto de ${f.produto}`}
                    loading="lazy"
                  />
                  <span>
                    {f.produto}
                    <em>
                      {diaCurto(f.dia)}
                      {f.lote ? ` · lote ${f.lote}` : ''} · {f.ok ? 'bateu' : 'não bateu'}
                    </em>
                  </span>
                </a>
              ))}
            </div>
          )}
          {pvSemFoto > 0 && (
            <p className="cf-info">
              {pvSemFoto} pré-vencido(s) sem foto: as vendas que vieram do 1.0 recebem a foto na
              carga final (a pasta de fotos do 1.0 entra junto).
            </p>
          )}
        </>
      )}
    </>
  );
}

type VendaEditavel = {
  qtd: number;
  dia: string;
  /** Preço unitário da venda; null = o servidor mantém o que estava. */
  preco: number | null;
  programa: Programa;
  /** No "dia errado": o dia em que o HOS registrou a venda (a correção de um clique). */
  diaNoHos?: string | undefined;
};

/** Editar e Cancelar de uma linha. Editar é de UM lançamento; o grupo se cancela junto. */
function Acoes({
  ids,
  jaPaga,
  venda,
  pode,
  podeEditar,
  sessao,
  aoMudar,
}: {
  ids: readonly string[];
  jaPaga: boolean;
  venda: VendaEditavel;
  pode: boolean;
  podeEditar: boolean;
  sessao: Sessao;
  aoMudar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const id = ids.length === 1 ? ids[0] : undefined;
  if (ids.length === 0 || (!pode && !podeEditar)) return null;
  if (jaPaga)
    return (
      <div className="cf-cancelar-nota">
        Semana já paga: para editar ou cancelar, estorne o pagamento na Premiação.
      </div>
    );
  if (editando && id)
    return (
      <Editar
        id={id}
        venda={venda}
        sessao={sessao}
        aoVoltar={() => setEditando(false)}
        aoSalvar={() => {
          setEditando(false);
          aoMudar();
        }}
      />
    );
  return (
    <div className="vp-acoes">
      {podeEditar &&
        (id ? (
          <button type="button" className="vp-editar-abrir" onClick={() => setEditando(true)}>
            <Icone nome="edit" tamanho={12} traco={2} /> Editar venda
          </button>
        ) : (
          <span className="vp-nota">
            {ids.length} lançamentos juntos: edite um a um em Ver os lançamentos
          </span>
        ))}
      <Cancelar ids={ids} jaPaga={jaPaga} pode={pode} sessao={sessao} aoCancelar={aoMudar} />
    </div>
  );
}

/**
 * Corrigir quantidade, dia ou preço. A original não se reescreve: vira cancelada ("corrigida:
 * motivo") e nasce a venda nova, com o prêmio recalculado pelas regras do dia da venda.
 */
function Editar({
  id,
  venda,
  sessao,
  aoVoltar,
  aoSalvar,
}: {
  id: string;
  venda: VendaEditavel;
  sessao: Sessao;
  aoVoltar: () => void;
  aoSalvar: () => void;
}) {
  const precoInicial = venda.preco === null ? '' : venda.preco.toFixed(2).replace('.', ',');
  const [qtd, setQtd] = useState(String(venda.qtd));
  const [dia, setDia] = useState(venda.dia);
  const [preco, setPreco] = useState(precoInicial);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  // vitamina paga pela faixa do card: o preço não se edita aqui
  const comPreco = venda.programa !== 'vitamina';
  const q = Number(qtd);
  const qtdOk = Number.isInteger(q) && q >= 1 && q <= 9999;
  // "1.234,56" ou "12,90" (vírgula decimal) ou "12.90"
  const precoNovo = preco.includes(',')
    ? preco.trim().replace(/\./g, '').replace(',', '.')
    : preco.trim();
  const precoOk = !comPreco || preco.trim() === '' || /^\d+(\.\d{1,2})?$/.test(precoNovo);
  const mudou = q !== venda.qtd || dia !== venda.dia || (comPreco && preco.trim() !== precoInicial);
  const pronto = qtdOk && precoOk && mudou && motivo.trim().length >= 3 && !enviando;

  const salvar = async () => {
    setEnviando(true);
    setErro(null);
    const r = await corrigirVenda(sessao, id, {
      ...(q !== venda.qtd ? { quantidade: q } : {}),
      ...(dia !== venda.dia ? { dia } : {}),
      ...(comPreco && preco.trim() !== precoInicial && preco.trim() !== ''
        ? { precoUnitario: precoNovo }
        : {}),
      motivo: motivo.trim(),
    });
    setEnviando(false);
    if (r.ok) aoSalvar();
    else setErro(r.mensagem);
  };

  return (
    <form
      className="vp-editar"
      onSubmit={(e) => {
        e.preventDefault();
        if (pronto) void salvar();
      }}
    >
      <div className="vp-editar-campos">
        <label>
          Quantidade
          <input
            inputMode="numeric"
            value={qtd}
            onChange={(e) => setQtd(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label>
          Dia da venda
          <input type="date" value={dia} max={hoje} onChange={(e) => setDia(e.target.value)} />
        </label>
        {comPreco && (
          <label>
            Preço unitário (R$)
            <input
              inputMode="decimal"
              value={preco}
              placeholder="o mesmo"
              onChange={(e) => setPreco(e.target.value)}
            />
          </label>
        )}
      </div>
      {venda.diaNoHos && dia !== venda.diaNoHos && (
        <button type="button" className="vp-dica" onClick={() => setDia(venda.diaNoHos ?? dia)}>
          O HOS registrou esta venda em {diaCurto(venda.diaNoHos)}: usar esse dia
        </button>
      )}
      <input
        value={motivo}
        maxLength={500}
        placeholder="Motivo da correção (obrigatório)"
        aria-label="Motivo da correção"
        onChange={(e) => setMotivo(e.target.value)}
      />
      <div className="vp-editar-botoes">
        <button type="submit" className="vp-salvar" disabled={!pronto}>
          {enviando ? 'Salvando…' : 'Salvar correção'}
        </button>
        <button type="button" onClick={aoVoltar} disabled={enviando}>
          Voltar
        </button>
      </div>
      {!qtdOk && <span className="cf-cancelar-erro">Quantidade: um número de 1 a 9999.</span>}
      {!precoOk && <span className="cf-cancelar-erro">Preço: use o formato 12,90.</span>}
      {erro && (
        <span className="cf-cancelar-erro" role="alert">
          {erro}
        </span>
      )}
      <span className="vp-nota">
        A venda original fica guardada como corrigida; a nova recebe o prêmio pelas regras do dia da
        venda.
      </span>
    </form>
  );
}

/** Cancelar o lançamento (todos do grupo), com motivo. O servidor recusa semana já paga. */
function Cancelar({
  ids,
  jaPaga,
  pode,
  sessao,
  aoCancelar,
}: {
  ids: readonly string[];
  jaPaga: boolean;
  pode: boolean;
  sessao: Sessao;
  aoCancelar: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!pode || ids.length === 0) return null;
  if (jaPaga)
    return (
      <div className="cf-cancelar-nota">
        Semana já paga: para cancelar, estorne o pagamento na Premiação.
      </div>
    );
  if (!aberto)
    return (
      <button type="button" className="cf-cancelar-abrir" onClick={() => setAberto(true)}>
        <Icone nome="x" tamanho={12} traco={2} /> Cancelar lançamento
        {ids.length > 1 ? ` (${String(ids.length)})` : ''}
      </button>
    );
  const enviar = async () => {
    setEnviando(true);
    setErro(null);
    for (const id of ids) {
      const r = await cancelarVenda(sessao, id, motivo.trim());
      // "já foi cancelada" não é erro: o objetivo já está cumprido
      if (!r.ok && !/já foi cancelada/i.test(r.mensagem)) {
        setErro(r.mensagem);
        setEnviando(false);
        return;
      }
    }
    setEnviando(false);
    setAberto(false);
    aoCancelar();
  };
  return (
    <form
      className="cf-cancelar"
      onSubmit={(e) => {
        e.preventDefault();
        if (motivo.trim().length >= 3 && !enviando) void enviar();
      }}
    >
      <input
        autoFocus
        value={motivo}
        maxLength={200}
        placeholder="Motivo do cancelamento (obrigatório)"
        aria-label="Motivo do cancelamento"
        onChange={(e) => setMotivo(e.target.value)}
      />
      <button
        type="submit"
        className="cf-cancelar-sim"
        disabled={motivo.trim().length < 3 || enviando}
      >
        {enviando ? 'Cancelando…' : 'Confirmar'}
      </button>
      <button type="button" onClick={() => setAberto(false)} disabled={enviando}>
        Voltar
      </button>
      {erro && (
        <span className="cf-cancelar-erro" role="alert">
          {erro}
        </span>
      )}
    </form>
  );
}

function Foto({ id, programa }: { id: string | null; programa: Programa }) {
  if (!id) return programa === 'pre_vencido' ? <div className="cf-sem-foto">sem foto</div> : null;
  return (
    <a className="cf-foto-mini" href={`/api/evidencias/${id}`} target="_blank" rel="noreferrer">
      <img src={`/api/evidencias/${id}`} alt="Foto do lançamento" loading="lazy" />
      <span>ver foto</span>
    </a>
  );
}
